import { Appointment } from "./index";
import {
  AppoinmentOutput,
  AppoinmentInput,
  AppointmentServiceCreateInput,
} from "./appoinment.types";
import type { PaymentMode, AppointmentStatus } from "./enums.types";

export interface PaginationMeta {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasNext: boolean;
  hasPrev: boolean;
}

export interface AppointmentApiResponse {
  success: boolean;
  data: AppoinmentOutput[];
  message?: string;
  pagination?: PaginationMeta;
}

export interface AppointmentApiSingleResponse {
  success: boolean;
  data: AppoinmentOutput;
  message?: string;
}

export interface AppointmentQueryParams {
  status?: string;
  customerId?: string;
  staffId?: string;
  serviceType?: string;
  startDate?: string;
  endDate?: string;
  search?: string;
  page?: number;
  limit?: number;
}

// AppointmentCreateInput is now aligned with API structure
export interface AppointmentCreateInput {
  type: "PACKAGE" | "USER_SUBSCRIPTION";
  typeId: string;
  date: string; // ISO date string
  status: AppointmentStatus;
  paymentMode: PaymentMode;
  amount: number | null;
  /**
   * Backend price adjustment. NOTE the inverted sign: a POSITIVE value reduces
   * the amount owed (discount), a NEGATIVE value increases it (surcharge).
   * Convert with toBackendDiscount/toUiAdjustment from @/lib/utils.
   */
  discount?: number;
  customerId: string;
  vehicleIds: string[];
  staffId: string | null;
  address: string | null;
  remarks: string | null; // Reason for postponed/cancelled/not-completed
  ruleId: string | null; // Only for USER_SUBSCRIPTION
  bookingType?: "ONLINE" | "OFFLINE" | "SUBSCRIPTION"; // Booking source type
  recurringDay?: string;
  preferredTime?: string;
  bookingRef?: string; // Optional - auto-generated if not provided
  repeatedAppointmentId?: string | null; // Link to parent RepeatedAppointment setup
  subtotal?: number; // Custom amount to override calculated price
}

export interface AppointmentUpdateInput
  extends Partial<AppointmentCreateInput> {
  status?: AppointmentStatus;
  featureIds?: string[];
}

// Helper function to transform API response to frontend Appointment type
export function transformAppointmentFromApi(
  apiAppointment: AppoinmentOutput,
  additionalData?: Partial<Appointment>,
): Appointment {
  return {
    id: apiAppointment.id,
    bookingRef: apiAppointment.bookingRef ?? null,
    type: apiAppointment.type,
    typeId: apiAppointment.typeId,
    serviceName: apiAppointment.serviceName,
    customerName: apiAppointment.customerName ?? null,
    staffName: apiAppointment.staffName ?? null,
    isStaffDeleted: apiAppointment.isStaffDeleted ?? false,
    date: new Date(apiAppointment.date),
    status: apiAppointment.status as Appointment["status"],
    paymentMode: apiAppointment.paymentMode,
    amount: apiAppointment.amount ?? null,
    discount: apiAppointment.discount ?? null,
    bookingType: apiAppointment.bookingType ?? null,
    platinumDiscount: apiAppointment.platinumDiscount ?? null,
    isDeleted: apiAppointment.isDeleted,
    createdAt: apiAppointment.createdAt,
    updatedAt: apiAppointment.updatedAt,
    customerId: apiAppointment.customerId,
    staffId: apiAppointment.staffId ?? null,
    vehicleIds: apiAppointment.vehicleIds ?? [],
    address: apiAppointment.address ?? null,
    remarks: apiAppointment.remarks ?? null,
    ruleId: apiAppointment.ruleId ?? null,
  };
}

// Helper function to transform frontend Appointment to API input
export function transformAppointmentToApi(
  appointment: Partial<Appointment & { featureIds?: string[] }>,
): Partial<AppoinmentInput | AppointmentServiceCreateInput> {
  const result: any = {
    type: appointment.type,
    date:
      appointment.date instanceof Date
        ? appointment.date.toISOString()
        : appointment.date,
    status: appointment.status,
    paymentMode: appointment.paymentMode,
    typeId: appointment.typeId || "",
    vehicleIds: appointment.vehicleIds || [],
    customerId: appointment.customerId || "",
    staffId: appointment.staffId || "",
    ruleId: appointment.ruleId || null,
    remarks: appointment.remarks || null,
  };

  if (appointment.featureIds !== undefined) {
    result.featureIds = appointment.featureIds;
  }

  if ((appointment as any).bookingType !== undefined) {
    result.bookingType = (appointment as any).bookingType;
  }

  return result;
}

// Status mapping functions - no longer needed as we use API values directly
// Keeping for backward compatibility if needed elsewhere, but not used in transformations
